/**
 * Helpers puros para dar de alta en la ruta de mostrador los pedidos de una
 * entrada finalizada: construcción de la dirección y del Sticker a partir de
 * la respuesta de OrderService.getCustomerOrder. Reproduce literalmente la
 * lógica de handleConfirmFinalizar del legacy (views/ColasGestorLlamamientos.js).
 * No importa servicios HTTP.
 */
import Sticker from "../../../models/Sticker";
import Utils from "../../../utils/Utils";

const ERROR_SIN_PEDIDO = "No se encontró información del pedido/cliente.";
const ERROR_SIN_TRANSPORTISTA = "No se ha podido resolver el transportista.";

/**
 * Normaliza la respuesta de getCustomerOrder: si es un array, el primer elemento
 * @throws {Error} Si no hay pedido
 */
export const normalizarPedido = (respuesta) => {
    const pedido = Array.isArray(respuesta) ? respuesta[0] : respuesta;
    if (!pedido) throw new Error(ERROR_SIN_PEDIDO);
    return pedido;
};

const primerNoVacio = (...valores) => valores.find((v) => !Utils.isFullEmpty(v));

/**
 * Construye la dirección base del sticker a partir del pedido ya normalizado.
 * Nombre: DIRECCION.NOMBRE, si vacío NOMBRE2, si vacío ClientesModel.NOMBRE.
 * Teléfono: DIRECCION.TELEFONO, si vacío el primero de TelfCliModels.
 * Tolera DIRECCION, ClientesModel y TelfCliModels ausentes.
 */
export const construirDireccion = (pedido) => {
    const dir = pedido?.DIRECCION ?? {};
    const cli = pedido?.ClientesModel ?? {};
    const telfs = Array.isArray(cli?.TelfCliModels) ? cli.TelfCliModels : [];

    return {
        name: primerNoVacio(dir?.NOMBRE, dir?.NOMBRE2) ?? (cli?.NOMBRE ?? ""),
        address: dir?.DIRECCION ?? "",
        cp: dir?.CODPOS ?? "",
        county: dir?.POBLACION ?? "",
        state: dir?.PROVINCIA ?? "",
        phone: primerNoVacio(dir?.TELEFONO, telfs[0]?.TELEFONO) ?? "",
        linea: dir?.LINEA ?? "",
        listCustomerAdress: cli?.EnviCliModels ?? [],
    };
};

/**
 * Instancia el Sticker con el constructor posicional de 26 argumentos. Es el
 * ÚNICO punto con la construcción posicional; el orden es el del legacy y no
 * debe alterarse. Posiciones:
 *  1 id ('serie-order')   2 serie            3 order           4 customer (nombre cliente)
 *  5 packages (0)         6 agency (código)  7 nameAgency      8 name
 *  9 address             10 cp              11 county         12 state
 * 13 delivery (ruta.id)  14 nameDelivery     15 phone          16 employee (código)
 * 17 nameEmployee        18 check (false)    19 created (new Date())
 * 20 id_customer (pedido.CLIENTE)  21 way_pay (forma de pago)  22 expedicionAsigna (null)
 * 23 dateDelivery (ruta.fecha)     24 counterDelivery (ruta.contador)
 * 25 dropshipping (false) 26 print (false)
 */
const instanciarSticker = ({ serie, order, pedido, direccion, ruta, transportista, empleado }) =>
    new Sticker(
        `${serie}-${order}`,
        serie,
        order,
        pedido?.ClientesModel?.NOMBRE ?? "",
        0,
        transportista.CODIGO,
        transportista.NOMBRE,
        direccion.name,
        direccion.address,
        direccion.cp,
        direccion.county,
        direccion.state,
        ruta?.id ?? "",
        ruta?.nombre ?? "",
        direccion.phone,
        empleado.codigo,
        empleado.nombre,
        false,
        new Date(),
        pedido?.CLIENTE ?? null,
        pedido?.FPagModel?.NOMBRE ?? null,
        null,
        ruta?.fecha ?? null,
        ruta?.contador ?? null,
        false,
        false
    );

/**
 * Construye el Sticker de un pedido para registrarlo en la ruta de mostrador.
 * @param {Object} params
 * @param {string} params.serie - Serie del albarán
 * @param {string} params.order - Número del albarán
 * @param {Object|Array} params.pedido - Respuesta de getCustomerOrder
 * @param {Object} [params.direccion] - Dirección ya construida (si no, se calcula)
 * @param {Object} params.ruta - Ruta de mostrador { id, nombre, fecha, contador }
 * @param {Object} params.transportista - { CODIGO, NOMBRE }
 * @param {{codigo: *, nombre: string}} params.empleado - Empleado resuelto
 * @returns {Sticker} Sticker listo para StickerService.registerSticker
 * @throws {Error} Si no hay pedido o no hay transportista.CODIGO
 */
export const construirSticker = ({ serie, order, pedido, direccion, ruta, transportista, empleado }) => {
    const datos = normalizarPedido(pedido);
    if (!transportista?.CODIGO) throw new Error(ERROR_SIN_TRANSPORTISTA);

    const dir = direccion ?? construirDireccion(datos);
    const sticker = instanciarSticker({
        serie, order, pedido: datos, direccion: dir, ruta, transportista, empleado,
    });

    sticker.listCustomerAdress = dir.listCustomerAdress;
    sticker.weight = 0;
    sticker.boxes = 0;
    sticker.isNew = false;
    sticker.linea = dir.linea;
    sticker.departamento = 0;
    return sticker;
};
